import { i18n } from 'app/utils/i18n';
import { VariableDefinition, getUnitSuffix } from '../variable-schema';
import { sanitizeColorValue } from '../utils/scss-formatting';
import { BackgroundImagePicker } from './BackgroundImagePicker';
import { CopyVariable } from './CopyVariable';
import { FontField } from './FontField';
import { NumberInput } from './NumberInput';
import './VariableField.scss';

interface VariableFieldProps {
  def: VariableDefinition;
  value: string | number;
  onChange: (key: string, value: string) => void;
  onFieldHover?: (key: string | null) => void;
  variables?: Record<string, unknown>;
}

export function VariableField({ def, value, onChange, onFieldHover, variables }: VariableFieldProps) {
  const hoverProps = onFieldHover ? { onMouseEnter: () => onFieldHover(def.key), onMouseLeave: () => onFieldHover(null) } : {};
  const label = i18n(def.label);
  const suffix = getUnitSuffix(def.type);

  if (def.type === 'image') {
    return <BackgroundImagePicker value={String(value)} onChange={onChange} variableKey={def.key} />;
  }

  if (def.type === 'font') {
    return <FontField def={def} value={value} onChange={onChange} onFieldHover={onFieldHover} variables={variables} />;
  }

  if (def.type === 'select' && def.options) {
    return (
      <div className="variable-field d-flex align-items-center gap-2" {...hoverProps}>
        <label className="variable-field__label fs-7">{label}</label>
        <select
          className="form-select form-select-sm variable-field__select"
          value={String(value)}
          disabled={!def.editable}
          onChange={(e) => onChange(def.key, e.target.value)}
        >
          {def.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {i18n(opt.label)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (def.type === 'color') {
    return (
      <div className="variable-field d-flex align-items-center gap-2" {...hoverProps}>
        <input
          type="color"
          className="variable-field__color-swatch"
          value={String(value) || '#000000'}
          disabled={!def.editable}
          onChange={(e) => onChange(def.key, e.target.value)}
        />
        <input
          type="text"
          className="form-control form-control-sm variable-field__color-input"
          value={String(value)}
          disabled={!def.editable}
          onChange={(e) => onChange(def.key, sanitizeColorValue(e.target.value))}
        />
        <span className="fs-7">{label}</span>
        {!def.editable && <CopyVariable variableKey={def.key} />}
      </div>
    );
  }

  return (
    <div className="variable-field d-flex align-items-center gap-2" {...hoverProps}>
      <label className="variable-field__label fs-7">{label}</label>
      <NumberInput def={def} value={value} onChange={onChange} />
      {suffix && <span className="text-muted fs-7">{suffix}</span>}
    </div>
  );
}
